import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { i18nFilter } from "app/filters/i18nFilter";
import { ConversionSegmentsUtils } from "app/pages/conversion/ConversionSegmentsUtils";
import { WebsiteSegmentEmptyState } from "app/pages/conversion/oss/ConversionSegmentOSS";
import { FlexContainerCentered, SecondaryText, StyledLink } from "app/pages/conversion/oss/ConversionSegmentOSSStyles";
import CountryService from "app/services/CountryService";
import { BoxContainer } from "pro-features/pages/conversion/components/ConversionScatterChart/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { ISegmentsData } from "../../../app/services/conversion/ConversionSegmentsService";
import { StyledPageTitle } from "../../styled components/StyledPageTitle/src/StyledPageTitle";

import AllContexts from "./components/AllContexts";
import { WebsiteConversionOvertime } from "./components/benchmarkOvertime/WebsiteConversionOvertime";
import { MultiLobIndication } from "./MultiLobIndication";
import { ConversionContainer } from "./StyledComponents";

interface IConversionSegmentOverviewProps {
    pageFilters?: any;
    loading: boolean;
    data?: any;
    translate: (key: string) => string;
    track: (a?: FixmeType, b?: FixmeType, c?: FixmeType, d?: FixmeType) => void;
    graphData: any;
    durationDataForWidget: string[];
    onGraphDDClick: (props: FixmeType) => void;
    getLink: (a: FixmeType, b?: FixmeType, c?: FixmeType) => string;
    segmentsData: ISegmentsData;
    isSingleLob: boolean;
    overviewSupportedCountries: number[];
}

export const ConversionSegmentOverview: FunctionComponent<IConversionSegmentOverviewProps> = ({
    translate,
    track,
    graphData,
    loading,
    pageFilters,
    durationDataForWidget,
    onGraphDDClick,
    getLink,
    isSingleLob,
    segmentsData,
    overviewSupportedCountries,
}) => {
    const websiteConversionOvertimeProps = useMemo(() => {
        return {
            title: "conversion.website.overtime.graph.title",
            titleTooltip: "conversion.website.overtime.graph.title.tooltip",
            filters: pageFilters,
            data: graphData,
            isLoading: loading,
            durationDataForWidget,
            onGraphDDClick,
            rowSelectionProp: "Domain",
            segmentsData,
        };
    }, [pageFilters, graphData, loading, segmentsData]);

    const renderEmptyState = () => {
        return (
            <BoxContainer data-automation-sites-vs-category={true}>
                <WebsiteSegmentEmptyState primaryText={"conversion.analysis.oss.invalid.country.title"}>
                    <FlexContainerCentered>
                        <SecondaryText>
                            {i18nFilter()("conversion.analysis.oss.invalid.country.subtitle")}
                        </SecondaryText>
                        {overviewSupportedCountries.map((country, index) => {
                            if (
                                overviewSupportedCountries.length > 1 &&
                                index === overviewSupportedCountries.length - 1
                            ) {
                                return (
                                    <>
                                        <SecondaryText>
                                            &nbsp;
                                            {i18nFilter()("conversion.analysis.oss.invalid.country.subtitle.or")}
                                        </SecondaryText>
                                        <StyledLink
                                            key={country}
                                            href={proNavigator.href("conversion-customsegement", {
                                                ...pageFilters,
                                                country,
                                                tab: 0,
                                            })}
                                        >
                                            {CountryService.getCountryById(country).text}
                                        </StyledLink>
                                    </>
                                );
                            } else {
                                return (
                                    <>
                                        {index > 0 && index <= overviewSupportedCountries.length - 2 ? (
                                            <SecondaryText>,</SecondaryText>
                                        ) : null}
                                        <StyledLink
                                            key={country}
                                            href={proNavigator.href("conversion-customsegement", {
                                                ...pageFilters,
                                                country,
                                                tab: 0,
                                            })}
                                        >
                                            {CountryService.getCountryById(country).text}
                                        </StyledLink>
                                    </>
                                );
                            }
                        })}
                    </FlexContainerCentered>
                </WebsiteSegmentEmptyState>
            </BoxContainer>
        );
    };

    const sector = segmentsData
        ? ConversionSegmentsUtils.getSegmentById(segmentsData, pageFilters.sid).segmentName
        : "";
    return (
        <AllContexts
            translate={translate}
            track={track}
            linkFn={getLink}
        >
            <ConversionContainer>
                <StyledPageTitle data-automation-page-title={true}>
                    {translate("conversion.website.overview.title")}
                </StyledPageTitle>
                {!isSingleLob ? <MultiLobIndication sector={sector} /> : null}
                {overviewSupportedCountries.includes(pageFilters.country) ? (
                    <WebsiteConversionOvertime {...websiteConversionOvertimeProps} />
                ) : (
                    renderEmptyState()
                )}
            </ConversionContainer>
        </AllContexts>
    );
};
